import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { config } from "./config";

// Minimal Google Identity Services typings — we only use the pieces we need.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            ux_mode?: "popup" | "redirect";
            use_fedcm_for_prompt?: boolean;
            itp_support?: boolean;
          }) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
          renderButton: (
            parent: HTMLElement,
            opts: Record<string, unknown>,
          ) => void;
        };
      };
    };
  }
}

type GoogleUser = {
  idToken: string;
  email: string;
  name: string;
  picture?: string;
  exp: number;
};

type AuthContextValue = {
  user: GoogleUser | null;
  ready: boolean;
  signIn: () => void;
  signOut: () => void;
  renderGoogleButton: (el: HTMLElement) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = "predabet.idtoken.v1";

function decodeJwt(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  if (!payload) return {};
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "===".slice((normalized.length + 3) % 4);
  try {
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

function userFromToken(token: string): GoogleUser | null {
  const claims = decodeJwt(token);
  const exp = Number(claims.exp ?? 0);
  if (!exp || exp * 1000 < Date.now()) return null;
  return {
    idToken: token,
    email: String(claims.email ?? ""),
    name: String(claims.name ?? claims.email ?? "user"),
    picture: typeof claims.picture === "string" ? claims.picture : undefined,
    exp,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<GoogleUser | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? userFromToken(stored) : null;
  });
  const [ready, setReady] = useState(() => !config.googleClientId);

  const handleCredential = useCallback((credential: string) => {
    const parsed = userFromToken(credential);
    if (!parsed) return;
    localStorage.setItem(STORAGE_KEY, credential);
    setUser(parsed);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const clientId = config.googleClientId;
    if (!clientId) {
      return;
    }

    const init = () => {
      if (cancelled || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => handleCredential(response.credential),
        auto_select: false,
        ux_mode: "popup",
        // Required under Chrome's third-party cookie deprecation; without this
        // `prompt()` is silently suppressed in many browsers.
        use_fedcm_for_prompt: true,
        itp_support: true,
      });
      queueMicrotask(() => {
        if (!cancelled) setReady(true);
      });
    };

    if (window.google) {
      init();
    } else {
      const interval = window.setInterval(() => {
        if (window.google) {
          window.clearInterval(interval);
          init();
        }
      }, 100);
      window.setTimeout(() => window.clearInterval(interval), 10_000);
    }

    return () => {
      cancelled = true;
    };
  }, [handleCredential]);

  // Programmatic entry point, kept for callers that want to trigger auth from
  // somewhere other than the header button (e.g. a "sign in to continue" CTA).
  // The primary UX is the GIS-rendered button that SignInButton.tsx overlays
  // beneath our themed pill — that button is click-driven, FedCM-compatible,
  // and notably does NOT depend on Google's `/gsi/log` telemetry endpoint,
  // which adblocker blocklists routinely block (visible in DevTools as
  // `ERR_BLOCKED_BY_CLIENT` on POSTs to accounts.google.com/gsi/log).
  const signIn = useCallback(() => {
    if (!window.google || !config.googleClientId) return;
    window.google.accounts.id.prompt();
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    window.google?.accounts.id.disableAutoSelect();
    setUser(null);
  }, []);

  // Auto-sign-out when the token expires while the page is open.
  useEffect(() => {
    if (!user) return;
    const msUntilExpiry = user.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      queueMicrotask(() => signOut());
      return;
    }
    const timer = window.setTimeout(signOut, msUntilExpiry);
    return () => window.clearTimeout(timer);
  }, [user, signOut]);

  const renderGoogleButton = useCallback((el: HTMLElement) => {
    if (!window.google || !config.googleClientId) return;
    el.innerHTML = "";
    // This button lives inside a cross-origin iframe, so we can't theme it
    // past GIS's preset options. That's fine here because the caller lays our
    // own themed pill *underneath* this iframe and paints this element at
    // opacity 0 — the iframe still receives pointer and keyboard events so
    // Google's real sign-in flow fires, we just don't render its chrome.
    window.google.accounts.id.renderButton(el, {
      type: "standard",
      theme: "filled_black",
      size: "large",
      text: "signin_with",
      shape: "pill",
      logo_alignment: "left",
      width: 220,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, ready, signIn, signOut, renderGoogleButton }),
    [user, ready, signIn, signOut, renderGoogleButton],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
